/**
 * PayoffChart — strategy payoff diagram at expiry.
 *
 * Props
 * ─────
 * strategyType  "PYRAMID_HEDGING" | "DELTA_HEDGED_SHORT_STRANGLE"
 * derived       strategy_json.derived
 * reference     strategy_json.reference  (optional, used by DHSS for ltp_at_init)
 * intent        strategy_json.intent     (optional, used by DHSS for delta ranges)
 * title         chart title string       (optional)
 */
import React, { useMemo } from "react";
import {
  ResponsiveContainer, ComposedChart, Line, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Label
} from "recharts";

// ───────────────────────────────────────────────────────���─────────────────────
// PYRAMID payoff computation
// ────────────────────────────────────���────────────────────────────────────────
function computePyramidPayoff(derived) {
  const {
    ladder = [],
    call_strike,
    call_ltp = 0,
    lot_size = 1,
    target_lots = 1,
    reference_avg_price,
    initial_qty,
    initial_avg_price,
    csp_enabled = false,
    put_strike = 0,
    put_ltp = 0,
    options_income,   // actual premium collected; overrides call_ltp × call_qty when set
  } = derived;

  if (!call_strike) return null;

  const call_qty = target_lots * lot_size;
  const put_qty  = csp_enabled ? lot_size : 0;
  // Use actual collected premium when available (existing strategy);
  // fall back to call_ltp × call_qty as an estimate for fresh previews.
  const effective_call_income = (options_income != null && options_income > 0)
    ? options_income
    : call_ltp * call_qty;

  // Initial position (before any ladder step triggers)
  const init_qty = initial_qty != null ? initial_qty
    : (ladder.length > 0 ? ladder[0].total_qty - ladder[0].qty : call_qty);
  const init_avg = initial_avg_price != null ? initial_avg_price : reference_avg_price;

  // Helper: compute P&L at a given price
  const pnlAt = (price) => {
    const triggered = ladder.filter(s => s.buy_price <= price);
    let qty, avg;
    if (triggered.length > 0) {
      const last = triggered[triggered.length - 1];
      qty = last.total_qty;
      avg = last.avg_price;
    } else {
      qty = init_qty;
      avg = init_avg;
    }
    const stock_pnl = (price - avg) * qty;
    const call_pnl  = effective_call_income - Math.max(0, price - call_strike) * call_qty;
    const put_pnl   = csp_enabled && put_strike
      ? put_ltp * put_qty - Math.max(0, put_strike - price) * put_qty
      : 0;
    return Math.round(stock_pnl + call_pnl + put_pnl);
  };

  // Helper: find breakevens (sign changes) in a set of points
  const findBreakevens = (pts) => {
    const bes = [];
    for (let i = 1; i < pts.length; i++) {
      if (Math.sign(pts[i - 1].pnl) !== Math.sign(pts[i].pnl)) {
        const p0 = pts[i - 1], p1 = pts[i];
        const be = Math.round(p0.price + (p1.price - p0.price) * (-p0.pnl / (p1.pnl - p0.pnl)));
        bes.push(be);
      }
    }
    return bes;
  };

  // X-axis: always show 4% above call_strike and 4% below put_strike (when CSP).
  // Without CSP, show 10% below the lower anchor (ladder start or init avg).
  const ladder_low  = ladder.length ? ladder[0].buy_price : init_avg;
  const p_max = Math.round(call_strike * 1.04 / 10) * 10;
  const p_min = (csp_enabled && put_strike)
    ? Math.round(put_strike * 0.96 / 10) * 10
    : Math.round(Math.min(ladder_low, init_avg) * 0.90 / 10) * 10;

  // Second pass: final points over restricted range
  const numSteps = 80;
  const stepSize = (p_max - p_min) / numSteps;
  const points = [];
  for (let i = 0; i <= numSteps; i++) {
    const price = Math.round(p_min + i * stepSize);
    points.push({ price, pnl: pnlAt(price) });
  }

  const breakevens = findBreakevens(points);
  const max_pnl    = Math.max(...points.map(d => d.pnl));
  const max_pt     = points.find(d => d.pnl === max_pnl) || { price: call_strike };

  const keyLines = [
    { price: call_strike, label: `Call $${call_strike}`, color: "#6f42c1" },
  ];
  if (csp_enabled && put_strike) {
    keyLines.push({ price: put_strike, label: `Put $${put_strike}`, color: "#dc3545" });
  }

  const noteCSP = csp_enabled && put_strike ? ` · Put $${put_strike} sold @ LTP $${put_ltp}` : "";

  return {
    points,
    breakevens,
    maxProfit: { price: max_pt.price, pnl: max_pnl },
    keyLines,
    theoretical: false,
    note: `Step-by-step accumulation · ${target_lots} lot(s) · Call $${call_strike} · Options income $${Math.round(effective_call_income).toLocaleString("en-US")}${noteCSP}`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DHSS (Short Strangle) payoff computation
// Uses actual contract data from build when available; falls back to estimates
// ─────────────────────────────────────────────────────────────────────────────
function computeStranglePayoff(derived, reference, intent) {
  const ltp = (reference && reference.underlying_ltp_at_init) || derived.underlying_ltp || 0;
  if (!ltp) return null;

  const lots     = derived.lots_per_side || intent?.lots_per_side || 1;
  const lot_size = derived.lot_size || 1;

  // Use actual contract data when build_strategy_state populated it
  const hasActual = derived.call_strike && derived.put_strike &&
                    derived.call_ltp    && derived.put_ltp;

  let callStrike, putStrike, callPrem, putPrem, theoretical;

  if (hasActual) {
    callStrike  = derived.call_strike;
    putStrike   = derived.put_strike;
    callPrem    = derived.call_ltp;
    putPrem     = derived.put_ltp;
    theoretical = false;
  } else {
    const callRange = derived.call_delta_range || intent?.call_delta_range || [0.15, 0.25];
    const putRange  = derived.put_delta_range  || intent?.put_delta_range  || [0.15, 0.25];
    const callDelta = (callRange[0] + callRange[1]) / 2;
    const putDelta  = (putRange[0]  + putRange[1])  / 2;
    const callOtm   = Math.max(0.04, (0.33 - callDelta) * 0.65);
    const putOtm    = Math.max(0.04, (0.33 - putDelta)  * 0.65);
    callStrike  = Math.round(ltp * (1 + callOtm) / 50) * 50;
    putStrike   = Math.round(ltp * (1 - putOtm)  / 50) * 50;
    callPrem    = Math.round(callDelta * ltp * 0.12);
    putPrem     = Math.round(putDelta  * ltp * 0.12);
    theoretical = true;
  }

  const totPrem = callPrem + putPrem;
  const qty     = lot_size * lots;   // total shares across lots

  const p_min = Math.round(putStrike  * 0.70 / 50) * 50;
  const p_max = Math.round(callStrike * 1.30 / 50) * 50;
  const steps = 80;
  const inc   = (p_max - p_min) / steps;

  const points = [];
  for (let i = 0; i <= steps; i++) {
    const price    = Math.round(p_min + i * inc);
    const call_pnl = callPrem - Math.max(0, price - callStrike);
    const put_pnl  = putPrem  - Math.max(0, putStrike - price);
    points.push({ price, pnl: Math.round((call_pnl + put_pnl) * qty) });
  }

  const left_be  = Math.round(putStrike  - totPrem);
  const right_be = Math.round(callStrike + totPrem);
  const max_pnl  = Math.round(totPrem * qty);

  const noteBase = `CE $${callStrike} ($${callPrem}) + PE $${putStrike} ($${putPrem}) · ${lots} lot(s) × ${lot_size} shares`;

  return {
    points,
    breakevens: [left_be, right_be],
    maxProfit: { price: Math.round((callStrike + putStrike) / 2), pnl: max_pnl },
    keyLines: [
      { price: callStrike, label: `CE $${callStrike}`, color: "#28a745" },
      { price: putStrike,  label: `PE $${putStrike}`,  color: "#dc3545" },
    ],
    theoretical,
    note: theoretical ? `Estimated: ${noteBase}` : noteBase,
  };
}

// ─────────────────────────────────────────────────���──────────────────────��────
// Custom tooltip
// ─────────────────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const pnl = payload[0].value;
  return (
    <div style={{
      background: "#fff", border: "1px solid #ddd", borderRadius: 6,
      padding: "8px 12px", fontSize: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
    }}>
      <div style={{ color: "#555" }}>Price: <strong>${Number(label).toLocaleString("en-US")}</strong></div>
      <div style={{ color: pnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>
        P&L: {pnl >= 0 ? "+" : ""}${Number(pnl).toLocaleString("en-US")}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const PayoffChart = ({ strategyType, derived, reference, intent, title }) => {
  const result = useMemo(() => {
    if (!derived) return null;
    if (strategyType === "DELTA_HEDGED_SHORT_STRANGLE") {
      return computeStranglePayoff(derived, reference, intent);
    }
    return computePyramidPayoff(derived);
  }, [strategyType, derived, reference, intent]);

  if (!result) {
    return (
      <div style={{ color: "#888", fontSize: 13, padding: "12px 0" }}>
        Payoff chart unavailable — missing strike or ladder data.
      </div>
    );
  }

  const { points, breakevens, maxProfit, keyLines, theoretical, note } = result;

  // Compute where the zero line falls as a fraction (top→bottom) for gradient split
  const minPnl = Math.min(...points.map(d => d.pnl));
  const maxPnl = Math.max(...points.map(d => d.pnl));
  const zeroFrac = maxPnl > 0 && minPnl < 0
    ? (maxPnl / (maxPnl - minPnl))
    : maxPnl <= 0 ? 0 : 1;
  const zeroPercent = `${(zeroFrac * 100).toFixed(1)}%`;

  const yFmt = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (abs >= 1000)    return `${(v / 1000).toFixed(1)}K`;
    return String(v);
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <h6 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
          {title || "Payoff at Expiry"}
          {theoretical && (
            <span style={{
              fontSize: 10, color: "#fff", background: "#fd7e14",
              padding: "1px 6px", borderRadius: 10, marginLeft: 8, fontWeight: 400
            }}>Theoretical estimate</span>
          )}
        </h6>
        <span style={{ fontSize: 11, color: "#888" }}>
          Best case P&L: <strong style={{ color: maxPnl >= 0 ? "#28a745" : "#dc3545" }}>${maxPnl.toLocaleString("en-US")}</strong>
          &nbsp;|&nbsp;
          Max loss in view: <strong style={{ color: "#dc3545" }}>${minPnl.toLocaleString("en-US")}</strong>
        </span>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={points} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
          <defs>
            <linearGradient id={`pnlGrad_${strategyType}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset={zeroPercent}     stopColor="#28a745" stopOpacity={0.25} />
              <stop offset={zeroPercent}     stopColor="#dc3545" stopOpacity={0.25} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

          <XAxis dataKey="price" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1).toLocaleString("en-US")}`}>
            <Label value="Stock Price at Expiry" offset={-18} position="insideBottom" style={{ fontSize: 11, fill: "#666" }} />
          </XAxis>

          <YAxis tick={{ fontSize: 11 }} tickFormatter={yFmt} width={55}>
            <Label value="P&L ($)" angle={-90} position="insideLeft" offset={10} style={{ fontSize: 11, fill: "#666" }} />
          </YAxis>

          <Tooltip content={<CustomTooltip />} />

          {/* Zero line */}
          <ReferenceLine y={0} stroke="#666" strokeWidth={1.5} strokeDasharray="6 3" />

          {/* Breakeven lines */}
          {breakevens.map((be, i) => (
            <ReferenceLine key={`be${i}`} x={be} stroke="#ff7300" strokeWidth={1.5} strokeDasharray="5 4">
              <Label value={`BE $${be.toLocaleString("en-US")}`} position={i === 0 ? "insideTopLeft" : "insideTopRight"}
                style={{ fontSize: 10, fill: "#ff7300", fontWeight: 600 }} />
            </ReferenceLine>
          ))}

          {/* Key price lines (strikes, call level, etc.) */}
          {keyLines.map((kl, i) => (
            <ReferenceLine key={`kl${i}`} x={kl.price} stroke={kl.color} strokeWidth={1} strokeDasharray="4 3">
              <Label value={kl.label} position="insideTopRight"
                style={{ fontSize: 9, fill: kl.color }} />
            </ReferenceLine>
          ))}

          {/* Max profit marker */}
          {maxProfit && (
            <ReferenceLine x={maxProfit.price} stroke="#007bff" strokeWidth={1} strokeDasharray="3 3">
              <Label value={`Max +$${maxProfit.pnl.toLocaleString("en-US")}`} position="top"
                style={{ fontSize: 9, fill: "#007bff" }} />
            </ReferenceLine>
          )}

          {/* Filled area */}
          <Area
            type="monotone" dataKey="pnl"
            stroke="none"
            fill={`url(#pnlGrad_${strategyType})`}
          />

          {/* The actual payoff line */}
          <Line
            type="monotone" dataKey="pnl"
            stroke="#343a40" strokeWidth={2} dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {note && (
        <div style={{ fontSize: 10, color: "#888", textAlign: "center", marginTop: 2 }}>{note}</div>
      )}
    </div>
  );
};

export default PayoffChart;
